import * as React from "react";
import { Category } from "../types";
import useApi from "@/hooks/useApi";

export default function useCategories() {
  const [categories, setCategories] = React.useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = React.useState(true);
  const api = useApi();

  const fetchCategories = React.useCallback(async () => {
    setLoadingCategories(true);
    try {
      const response = await api.admin.get("api/kb/categories");
      const json = await response.json();
      setCategories(Array.isArray(json?.Categories) ? json.Categories : []);
    } catch (err) {
      console.error("Failed to fetch KB categories:", err);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  }, []);

  React.useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const addCategory = React.useCallback((category: Category) => {
    setCategories((oldState) => [...oldState, category]);
  }, []);

  const updateCategory = React.useCallback((category: Category) => {
    setCategories((oldState) => {
      const newState = [...oldState];
      const index = newState.findIndex((x) => x.Id === category.Id);

      if (index < 0) {
        return newState;
      }

      newState[index] = category;
      return newState;
    });
  }, []);

  const removeCategory = React.useCallback((id: string) => {
    setCategories((oldState) => {
      const newState = [...oldState];
      const index = newState.findIndex((x) => x.Id === id);

      if (index >= 0) {
        newState.splice(index, 1);
      }

      return newState;
    });

    try {
      api.admin.delete(`api/kb/categories?id=${id}`);
    } catch (err) {
      console.error("Failed to delete category:", err);
    }
  }, []);

  const moveCategoryUp = React.useCallback((index: number) => {
    setCategories((oldState) => {
      const newState = [...oldState];
      if (index <= 0 || index >= newState.length) return newState;
      const prevItem = newState[index - 1];
      const thisItem = newState[index];
      const tempPriority = prevItem.DisplayPriority;
      prevItem.DisplayPriority = thisItem.DisplayPriority;
      thisItem.DisplayPriority = tempPriority;
      return newState;
    });

    try {
      api.admin.post("api/kb/categories/move-up", { index });
    } catch (err) {
      console.error("Failed to move category up:", err);
    }
  }, []);

  const moveCategoryDown = React.useCallback((index: number) => {
    setCategories((oldState) => {
      const newState = [...oldState];
      if (index < 0 || index >= newState.length - 1) return newState;
      const thisItem = newState[index];
      const nextItem = newState[index + 1];
      const tempPriority = thisItem.DisplayPriority;
      thisItem.DisplayPriority = nextItem.DisplayPriority;
      nextItem.DisplayPriority = tempPriority;
      return newState;
    });

    try {
      api.admin.post("api/kb/categories/move-down", { index });
    } catch (err) {
      console.error("Failed to move category down:", err);
    }
  }, []);

  return {
    categories,
    loadingCategories,
    addCategory,
    updateCategory,
    fetchCategories,
    removeCategory,
    moveCategoryUp,
    moveCategoryDown
  };
}
